import { FuseMeteringSettings } from '../../components/forms/FuseProtectionForm'
import { PowerFeeSettings, powerFeeFromConfig, powerFeeToConfig } from '../../components/forms/PowerFeeForm'
import { DeviceHwID, ID, Port } from '../../types/models'
import { PortConfigOptimizer, OptimizerConfig, OptimizerPort, PortNumber, PortDestinationKind } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { RadioPortControl } from './RadioPortControl'

/**
 * Controller for optimizer.
 */
export class OptimizerPortControl extends PortControl<OptimizerPort, OptimizerConfig> {

  port: OptimizerPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as OptimizerPort
  }

  get group(): string { return this.app.t('PortGroup-Optimization') }

  getTypeName(): string { return this.app.t('PortType-Optimizer') }

  getDescription(): string {
    return this.app.t('Energy usage optimizer')
  }

  getConfigDefaults(): PortConfigOptimizer {
    return {
      enabled: true,
      enable_fuse_protection: true,
      enable_power_limit: false,
      current_limits: [25, 25, 25],
      fuse_curve: [170, 140, 125, 110, 100],
      power_limit: 15,
      power_limit_averaging_period: 60,
      multiopt_role: 'MULTIOPT_NO',
      multiopt_group_id: 0,
      // Tehomaksu (power fee), off. See PowerFeeForm.
      peak_price: 0,
      peak_hour_from: 0,
      peak_hour_to: 0,
      peak_weekday_mask: 0,
      peak_lookback_months: 0,
      peak_month_mask: 0,
    }
  }

  get isVisible() { return false }

  isMyPort(id: ID): boolean {
    const portCtrl = this.systemControl.portControlForId(id)

    return (portCtrl.device.hw_id === this.device.hw_id)
  }

  getDestinationKind(id: ID): PortDestinationKind {
    if (!id) {
      return {
        dest_kind: 'DEST_KIND_THIS_DEVICE',
        dest: 0,
        port: '0/0' as PortNumber
      }
    }

    const portCtrl = this.systemControl.portControlForId(id)

    if (this.isMyPort(id)) {
      return {
        dest_kind: 'DEST_KIND_THIS_DEVICE',
        dest: 0,
        port: portCtrl.port.port_number
      }
    }
    const radioCtrl: RadioPortControl = this.deviceControl.findPortByType('RADIO') as RadioPortControl
    const slaves: DeviceHwID[] = radioCtrl.getConfig('slave_devids')
    if (slaves) {
      const idx = slaves.indexOf(portCtrl.device.hw_id)
      if (idx >= 0) {
        return {
          dest_kind: 'DEST_KIND_RADIO_SLAVE',
          dest: idx,
          port: portCtrl.port.port_number
        }
      }
    }

    return {
      dest_kind: 'DEST_KIND_SERVER',
      dest: 0,
      port: portCtrl.port.port_number
    }
  }

  getPortFromKind(kind: PortDestinationKind): ID | '' {
    if (!kind) return ''

    if (kind.dest_kind === 'DEST_KIND_THIS_DEVICE') {
      const portCtrl = this.deviceControl.findPortByNumber(kind.port)
      return portCtrl ? portCtrl.port.id as ID : ''
    }

    if (kind.dest_kind === 'DEST_KIND_RADIO_SLAVE') {
      const radioCtrl: RadioPortControl = this.deviceControl.findPortByType('RADIO') as RadioPortControl
      const slaves: DeviceHwID[] = radioCtrl.getConfig('slave_devids')
      if (slaves) {
        const hwid = slaves[kind.dest]
        if (hwid) {
          const deviceCtrl = this.systemControl.getDeviceControlByHwId(hwid)
          const portCtrl = deviceCtrl.findPortByNumber(kind.port)
          return portCtrl?.port.id as ID
        }
      }
    }

    return ''
  }

  /**
   * Convert and save UI settings as optimizer port settings. The power fee owns
   * `enable_power_limit` / `power_limit` (the free power); a fee switched off keeps the
   * stored `power_limit`, and the unused `power_limit_averaging_period` is kept as is.
   */
  saveSettings(fuse: FuseMeteringSettings, powerFee: PowerFeeSettings) {
    const keep = {
      power_limit: this.getConfig('power_limit'),
      power_limit_averaging_period: this.getConfig('power_limit_averaging_period'),
    }
    this.setConfigDefaults()

    for (const [key, value] of Object.entries(keep)) {
      if (value !== undefined) this.setConfig(key, value)
    }
    this.setConfig('enable_fuse_protection', fuse.protectFuses)
    this.setConfig('current_limits', [fuse.fuseSize, fuse.fuseSize, fuse.fuseSize])
    for (const [key, value] of Object.entries(powerFeeToConfig(powerFee))) {
      this.setConfig(key, value)
    }
  }

  getFuseSettings(): FuseMeteringSettings {
    if (this.getConfig('current_limits') === undefined) {
      this.setConfigDefaults()
    }
    return {
      enabled: true,
      protectFuses: this.getConfig('enable_fuse_protection'),
      fuseSize: (this.getConfig('current_limits') as number[])[0],
    }
  }

  getPowerFeeSettings(): PowerFeeSettings {
    return powerFeeFromConfig({
      enable_power_limit: this.getConfig('enable_power_limit'),
      power_limit: this.getConfig('power_limit'),
      peak_price: this.getConfig('peak_price'),
      peak_hour_from: this.getConfig('peak_hour_from'),
      peak_hour_to: this.getConfig('peak_hour_to'),
      peak_weekday_mask: this.getConfig('peak_weekday_mask'),
      peak_lookback_months: this.getConfig('peak_lookback_months'),
      peak_month_mask: this.getConfig('peak_month_mask'),
    })
  }
}
